import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchThemeData } from '../admin/themeSlice';
import { setSession } from '../admin/sessionSlice';
import { setCompanyLogoUrl } from './uiSlice';
import { isBlankLogoValue, sanitizeCompanyLogoUrl, sanitizeUserCompanyLogo } from '../functions/companyLogoUrl';
import { sendReport, extractReportIdFromResponse, resolveReportsAuthToken } from '../functions/sendReport';
import { checkArrowUpPlayedApi, isArrowRunAlreadyCompleted } from '../functions/checkArrowUpPlayed';
import { fetchReport as fetchReportApi } from '../functions/fetchReport';

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

/** True when `id` can be treated as report id (not the same value as user id). */
function idIsDistinctReportId(id, userId) {
  if (id == null || String(id).trim() === '') return false;
  if (userId == null || String(userId).trim() === '') return true;
  return String(id).trim() !== String(userId).trim();
}

/** Never store login `id` / mistaken fields as report id when they equal the real user id (breaks sendReport PUT). */
function rejectReportIdIfUserId(reportIdCandidate, resolvedUserId) {
  if (reportIdCandidate == null || String(reportIdCandidate).trim() === '') return null;
  const s = String(reportIdCandidate).trim();
  const uid = resolvedUserId != null ? String(resolvedUserId).trim() : '';
  if (uid !== '' && s === uid) return null;
  return s;
}

/** Guest/org login payloads differ; pull report id from every shape we’ve seen. */
function pickReportIdFromLoginPayload(d, resolvedUserId) {
  if (!d || typeof d !== 'object') return null;
  const r = d.report;
  const ur = d.userReport;
  const inner = d.data && typeof d.data === 'object' ? d.data : null;
  const candidates = [
    d.reportId,
    d.report_id,
    d.reportid,
    d.gameReportId,
    d.game_report_id,
    d.userReportId,
    d.user_report_id,
    r && typeof r === 'object' ? r.id ?? r.reportId : null,
    ur && typeof ur === 'object' ? ur.id ?? ur.reportId : null,
    inner
      ? inner.reportId ??
        inner.report_id ??
        inner.reportid ??
        inner.gameReportId ??
        inner.game_report_id ??
        (idIsDistinctReportId(inner.id, inner.userId) ? inner.id : null)
      : null,
    idIsDistinctReportId(d.id, d.userId) ? d.id : null,
  ];
  for (const v of candidates) {
    const ok = rejectReportIdIfUserId(v, resolvedUserId);
    if (ok) return ok;
  }
  return null;
}

function stripReportIdIfSameAsUserId(user) {
  if (!user || typeof user !== 'object') return user;
  const uid = user.userId != null ? String(user.userId).trim() : '';
  const rid = user.reportId != null ? String(user.reportId).trim() : '';
  if (uid !== '' && rid !== '' && uid === rid) {
    return { ...user, reportId: null };
  }
  return user;
}

function buildDisplayName(d) {
  const raw = (x) => (x == null || x === 'null' ? '' : String(x).trim());
  const first = raw(d.firstName);
  const last = raw(d.lastName);
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  const n = raw(d.name);
  if (n) return n;
  return raw(d.email) || raw(d.employeeId) || raw(d.userId) || 'Guest';
}

/** Org API field names vary; prefer first non-blank. */
function pickOrganizationLogoPath(org) {
  if (!org || typeof org !== 'object') return null;
  const candidates = [
    org.companyLogo,
    org.company_logo,
    org.companyUrl,
    org.company_url,
    org.logo,
    org.logoUrl,
    org.logo_url,
    org.organizationLogo,
    org.organization_logo,
    org.imageUrl,
    org.image_url,
    org.companyImageUrl,
    org.company_image_url,
    org.brandLogo,
    org.brand_logo,
  ];
  for (const c of candidates) {
    if (!isBlankLogoValue(c)) return String(c).trim();
  }
  return null;
}

function pickLogoFromLoginPayload(d) {
  if (!d || typeof d !== 'object') return null;
  const candidates = [
    d.companyLogoUrl,
    d.company_logo_url,
    d.companyLogo,
    d.company_logo,
    d.companyUrl,
    d.company_url,
  ];
  for (const c of candidates) {
    if (!isBlankLogoValue(c)) return String(c).trim();
  }
  return null;
}

/** Relative key → S3 base URL; absolute http(s) URL → unchanged; blank → Extra Mile Play logo. */
function buildCompanyLogoDisplayUrl(pathOrUrl) {
  const fallback = process.env.REACT_APP_EM_LOGO;
  if (isBlankLogoValue(pathOrUrl)) return fallback;
  const raw = String(pathOrUrl).trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  // Match Sort & Toss: prefer S3_SOURCE as the base for relative logo keys.
  const s3Source = String(process.env.REACT_APP_S3_SOURCE || '').trim();
  const base =
    s3Source !== ''
      ? (s3Source.endsWith('/') ? s3Source.slice(0, -1) : s3Source)
      : String(process.env.REACT_APP_S3BUCKET_URL || '').replace(/\/+$/, '');
  const rel = raw.replace(/^\/+/, '');
  if (!base) return fallback;
  return `${base}/${rel}`;
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const otp = queryParams.get('otp');
      const dbNameFromUrl =
        queryParams.get('database') ||
        queryParams.get('databaseName') ||
        queryParams.get('db') ||
        queryParams.get('dbname') ||
        '';
      const lobbyFromUrl =
        queryParams.get('lobby') ||
        queryParams.get('lobbyName') ||
        '';
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');

      // Same as health-sprint: resume saved user only when explicit save flow and no fresh OTP.
      if (save && !otp) {
        const stored = localStorage.getItem('userData');
        if (!stored) return rejectWithValue('No saved user data found.');
        let userData = sanitizeUserCompanyLogo(JSON.parse(stored));
        userData = stripReportIdIfSameAsUserId(userData);
        try {
          localStorage.setItem('userData', JSON.stringify(userData));
        } catch {
          /* ignore */
        }
        dispatch(setSession({ sessionId: userData.sessionId, organizationId: userData.organizationId }));
        dispatch(fetchThemeData({ themeId: null }));
        dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
        return userData;
      }

      let userResponseData;
      let authSource = 'ORG_USER';
      /** Full JSON body from OTP login (reportId sometimes lives next to `data`, not inside). */
      let loginResponseEnvelope = null;
      if (demo) {
        console.log("working with demo now");
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                sessionId: 'demo'+Math.floor(Math.random() * 100000),
                organizationId: 'demo'+Math.floor(Math.random() * 100000),
                gameId: null,
                role:"demobypass",
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: process.env.REACT_APP_BASE_URL,
              }
            }
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const otp = queryParams.get('otp');
        const role = queryParams.get('role');
        if (!otp) return rejectWithValue('OTP is required for login.');

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          authSource = 'GUEST_USER';
        }

        const headers = {
          'client-secret': process.env.REACT_APP_FIREBASE_CLIENT_SECRET,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (response.data.message !== 'LOGIN SUCCESS') {
          return rejectWithValue('Login failed');
        }
        loginResponseEnvelope = response.data;
        response.data.data['role'] = authSource;
        userResponseData = response.data.data;
      }

      const resolvedUserId = String(userResponseData.userId || userResponseData.id || '').trim();
      const normalizedReportId =
        pickReportIdFromLoginPayload(userResponseData, resolvedUserId) ??
        (loginResponseEnvelope ? pickReportIdFromLoginPayload(loginResponseEnvelope, resolvedUserId) : null);

      // Store session and fetch theme (await so play-status check can scope by theme)
      dispatch(setSession({ sessionId: userResponseData.sessionId, organizationId: userResponseData.organizationId }));
      let themeNameForPlayCheck = null;
      try {
        await dispatch(fetchThemeData({ themeId: null })).unwrap();
        const st = getState();
        themeNameForPlayCheck = st.theme?.data?.themename || st.theme?.currentTheme || null;
      } catch {
        /* theme fetch non-fatal */
      }

      // Build user data to persist
      const userData = {
        userId: userResponseData.userId || userResponseData.id,
        token:
          userResponseData.token ||
          userResponseData.accessToken ||
          userResponseData.access_token ||
          "",
        name: buildDisplayName(userResponseData),
        email: userResponseData.email || userResponseData.employeeId,
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        reportId: normalizedReportId,
        source: demo ? 'DEMO' : authSource,
        expiry: Date.now() + 24 * 60 * 60 * 1000,
        role:userResponseData.role,
        companyLogoUrl: process.env.REACT_APP_EM_LOGO,
        backButtonRedirect: process.env.REACT_APP_BASE_URL,
        userPlayedCount:userResponseData.userPlayedCount
      };

      if (!demo) {
        let logoResolved = false;
        try {
          const orgRes = await axios.get(`${process.env.REACT_APP_ORG_API}/${userResponseData.organizationId}`);
          if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
            const org = orgRes.data?.organization ?? orgRes.data?.data?.organization ?? null;
            const logoPath = pickOrganizationLogoPath(org);
            userData.companyLogoUrl = buildCompanyLogoDisplayUrl(logoPath);
            logoResolved = true;
            userData.backButtonRedirect = `${process.env.REACT_APP_BASE_URL}/game-detail/${userResponseData.gameId}`;
          }
        } catch {
          /* keep EM logo; optional login payload below */
        }
        if (!logoResolved) {
          const fromLogin = pickLogoFromLoginPayload(userResponseData);
          if (fromLogin) {
            userData.companyLogoUrl = buildCompanyLogoDisplayUrl(fromLogin);
          }
        }
      }

      userData.companyLogoUrl = sanitizeCompanyLogoUrl(userData.companyLogoUrl);

      /** Firebase often returns JWT role `PLAYER`; align `user.role` with auth flow for report seed + Rules. */
      const srcNorm = String(userData.source || '').trim().toUpperCase();
      const roleNorm = String(userData.role || '').trim().toUpperCase();
      if (srcNorm === 'ORG_USER' && (roleNorm === 'PLAYER' || roleNorm === 'PLAYERS' || roleNorm === '')) {
        userData.role = 'ORG_USER';
      }
      if (srcNorm === 'GUEST_USER' && roleNorm !== 'GUEST_USER' && roleNorm !== 'ORG_USER') {
        userData.role = 'GUEST_USER';
      }

      /** OTP/login often omits reportId; seed for org + guest flows (`role` or `source` from authSource). */
      const roleUpper = String(userData.role || '').trim().toUpperCase();
      const sourceUpper = String(userData.source || '').trim().toUpperCase();
      const isOrgOrGuest =
        roleUpper === 'ORG_USER' ||
        roleUpper === 'GUEST_USER' ||
        sourceUpper === 'ORG_USER' ||
        sourceUpper === 'GUEST_USER';
      const needsReportSeed =
        !demo &&
        isOrgOrGuest &&
        (userData.reportId == null || String(userData.reportId).trim() === '');
      let fetchReportUnavailable = false;
      if (needsReportSeed) {
        const adminSessionToken = `${userData.sessionId}&${userData.organizationId}`;
        // Recover existing report first (avoid duplicate insert on re-login with same user).
        try {
          const existing = await fetchReportApi({
            userId: userData.userId,
            token: adminSessionToken,
            sessionId: userData.sessionId,
            organizationId: userData.organizationId,
            gameId: userData.gameId || '',
            email: userData.email || null,
            name: userData.name || null,
            database: dbNameFromUrl || null,
            databaseName: dbNameFromUrl || null,
            db: dbNameFromUrl || null,
            dbname: dbNameFromUrl || null,
            lobby: lobbyFromUrl || null,
            lobbyName: lobbyFromUrl || null,
          });
          const existingRid =
            rejectReportIdIfUserId(existing?.report?.reportId, userData.userId) ||
            rejectReportIdIfUserId(existing?.report?.id, userData.userId) ||
            rejectReportIdIfUserId(extractReportIdFromResponse(existing), userData.userId);
          if (existingRid) {
            userData.reportId = String(existingRid).trim();
          }
        } catch (e) {
          const msg = String(e?.message || '');
          // Backend fetchReport route is misconfigured for some DBs (e.g. missing `stat_ini` table).
          // In that case, skip login-time seed to avoid duplicate 0/00:00 inserts on every login.
          if (/stat_ini/i.test(msg) || /failed to fetch report/i.test(msg)) {
            fetchReportUnavailable = true;
          }
        }
      }
      // IMPORTANT: Do not create report rows during login.
      // Report creation/update happens later from Rules/Game flow only.

      userData.reportId =
        userData.reportId != null && String(userData.reportId).trim() !== ''
          ? String(userData.reportId).trim()
          : null;
      const coerced = stripReportIdIfSameAsUserId(userData);
      Object.assign(userData, coerced);

      dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
      localStorage.setItem('userData', JSON.stringify(userData));
      return userData;
    } catch (error) {
      return rejectWithValue(error.response?.data?.err?.message || error.message);
    }
  }
);
let initialUser = null;
try {
  const persisted = localStorage.getItem('userData');
  if (persisted) {
    const parsed = JSON.parse(persisted);
    initialUser = stripReportIdIfSameAsUserId(sanitizeUserCompanyLogo(parsed));
    if (initialUser) {
      const logoChanged = initialUser.companyLogoUrl !== parsed.companyLogoUrl;
      const ridP = parsed.reportId != null ? String(parsed.reportId).trim() : '';
      const ridI = initialUser.reportId != null ? String(initialUser.reportId).trim() : '';
      const reportIdSanitized = ridP !== ridI;
      if (logoChanged || reportIdSanitized) {
        try {
          localStorage.setItem('userData', JSON.stringify(initialUser));
        } catch {
          /* ignore */
        }
      }
    }
  }
} catch {
  initialUser = null;
}

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,         // <-- hydrate from storage
    status: initialUser ? 'succeeded' : 'idle',
    error: null
  },
  reducers: {
    logout: state => {
      state.user = null;
      localStorage.removeItem('userData');
    },
    /** After first `sendReport` POST, API may return `reportId` — store for subsequent PUTs. */
    mergeReportId: (state, action) => {
      const id = action.payload;
      if (!state.user || id == null || id === '') return;
      const uid = state.user.userId != null ? String(state.user.userId).trim() : '';
      if (uid !== '' && String(id).trim() === uid) return;
      state.user = { ...state.user, reportId: id };
      try {
        localStorage.setItem('userData', JSON.stringify(state.user));
      } catch {
        /* ignore */
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = stripReportIdIfSameAsUserId(sanitizeUserCompanyLogo(action.payload));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

export const { logout, mergeReportId } = authSlice.actions;
export default authSlice.reducer;